import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import { PrintProtocol, type PrinterProfile } from '@etiquetachef/types';
import { formatDateTime } from '@/lib/format';
import { useMembers } from '@/features/members/useMembers';
import { usePrinters } from '@/features/printers/usePrinters';

type Props = {
  tenantId: string;
};

/** Rótulo legível do protocolo (ESC_POS → "ESC/POS"; demais inalterados). */
function protocolLabel(protocol: PrintProtocol): string {
  return protocol === PrintProtocol.ESC_POS ? 'ESC/POS' : protocol;
}

/**
 * Aba de impressoras do tenant (admin, Cloudscape `Table`). Read-only: o admin
 * apenas observa a frota cadastrada — toda mutação (parear, calibrar, definir
 * padrão, excluir) acontece no Android, onde o código de impressão vive e é
 * congelado. Expõe os atributos de calibração em colunas para diagnóstico.
 *
 * Origem: sem issue — commit a55e77f4c, 2026-06-10 14:05 +0200.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (aba Impressoras).
 *
 * Dados: `tenants/{tenantId}/printers` (`usePrinters`) e
 * `tenants/{tenantId}/members` (`useMembers`, para nomes de quem cadastrou).
 */
export function PrintersTab({ tenantId }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const result = usePrinters(tenantId);
  // Nome de quem cadastrou cada impressora. Na conta de demonstração o app
  // filtra a lista por autor, então este painel é o único lugar que vê a frota
  // inteira — e sem o nome não dá para saber de quem é o quê.
  const membersResult = useMembers(tenantId);
  const memberNames: Record<string, string> =
    membersResult.kind === 'READY'
      ? Object.fromEntries(membersResult.members.map((m) => [m.uid, m.displayName || m.email || m.uid]))
      : {};

  if (result.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (result.kind === 'ERROR') {
    return <Alert type="error">{t('printers.errorLoad')}</Alert>;
  }

  const printers = result.printers;

  const columns: TableProps.ColumnDefinition<PrinterProfile>[] = [
    {
      id: 'name',
      header: t('printers.columns.name'),
      cell: (p): React.ReactNode => <Box fontWeight="bold">{p.name}</Box>,
      sortingField: 'name',
      isRowHeader: true,
    },
    {
      id: 'model',
      header: t('printers.columns.model'),
      cell: (p): React.ReactNode => p.model ?? '—',
    },
    {
      // A coluna deixou de ser "MAC" porque nem toda impressora tem um. O iOS
      // não enxerga o endereço do rádio (a Apple não o expõe a apps de
      // terceiros) e identifica o aparelho pelo número de série do firmware —
      // item B11 da #106. Enquanto isto mostrava só `macAddress`, uma
      // impressora de iPhone aparecia com a célula VAZIA no único painel que vê
      // a frota inteira, e quem desse suporte leria como dado corrompido.
      id: 'identity',
      header: t('printers.columns.identity'),
      cell: (p): React.ReactNode => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {p.macAddress ?? p.identity?.stableKey ?? '—'}
        </span>
      ),
    },
    {
      id: 'protocol',
      header: t('printers.columns.protocol'),
      cell: (p): React.ReactNode => protocolLabel(p.protocol),
    },
    {
      id: 'dpi',
      header: t('printers.columns.dpi'),
      cell: (p): React.ReactNode => p.dpi,
    },
    {
      id: 'size',
      header: t('printers.columns.size'),
      cell: (p): React.ReactNode =>
        p.labelWidthMm > 0 && p.labelHeightMm > 0
          ? `${String(p.labelWidthMm)}×${String(p.labelHeightMm)}mm`
          : '—',
    },
    {
      id: 'density',
      header: t('printers.columns.density'),
      cell: (p): React.ReactNode => p.density,
    },
    {
      id: 'speed',
      header: t('printers.columns.speed'),
      cell: (p): React.ReactNode => p.speed,
    },
    {
      id: 'calibration',
      header: t('printers.columns.calibration'),
      cell: (p): React.ReactNode =>
        p.wizardCompletedAt != null ? (
          <Badge color="green">{t('printers.badge.calibrated')}</Badge>
        ) : (
          <Badge color="red">{t('printers.badge.notCalibrated')}</Badge>
        ),
    },
    {
      id: 'labelCount',
      header: t('printers.columns.labelCount'),
      cell: (p): React.ReactNode => p.labelCount ?? 0,
    },
    {
      id: 'lastUsed',
      header: t('printers.columns.lastUsed'),
      cell: (p): React.ReactNode =>
        p.lastUsedAt != null ? formatDateTime(p.lastUsedAt, locale) : '—',
    },
    {
      id: 'status',
      header: t('printers.columns.status'),
      cell: (p): React.ReactNode =>
        p.isDefault ? <Badge color="green">{t('printers.badge.default')}</Badge> : '—',
    },
    {
      id: 'author',
      header: t('printers.columns.author'),
      cell: (p): React.ReactNode =>
        p.createdByUid != null && p.createdByUid.length > 0
          ? (memberNames[p.createdByUid] ?? p.createdByUid)
          : '—',
    },
  ];

  return (
    <SpaceBetween size="m">
      <Header variant="h2" description={t('printers.description')}>
        {t('printers.title')}
      </Header>

      {printers.length === 0 ? (
        <Container>
          <Box textAlign="center" padding="m" color="text-body-secondary">
            {t('printers.empty')}
          </Box>
        </Container>
      ) : (
        <Table<PrinterProfile>
          items={printers}
          trackBy="id"
          variant="container"
          columnDefinitions={columns}
          empty={
            <Box textAlign="center" color="inherit">
              {t('printers.empty')}
            </Box>
          }
        />
      )}
    </SpaceBetween>
  );
}
